import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Plus, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const input = "w-full h-10 px-3 rounded-lg bg-[#0B132B] border border-blue-500/20 focus:border-blue-500 outline-none text-sm mt-1";

export const JudgesTab = ({ onChange }) => {
  const [judges, setJudges] = useState([]);
  const [regs, setRegs] = useState([]);
  const [anns, setAnns] = useState([]);
  const [dips, setDips] = useState([]);
  const [cards, setCards] = useState([]);
  const [days, setDays] = useState([]);
  const load = () => api.get("/admin/judges").then((r) => { setJudges(r.data); onChange?.(); });
  const loadRegs = () => api.get("/admin/registrars").then((r) => setRegs(r.data));
  const loadAnns = () => api.get("/admin/announcers").then((r) => setAnns(r.data));
  const loadDips = () => api.get("/admin/diplomas").then((r) => setDips(r.data));
  useEffect(() => { load(); loadRegs(); loadAnns(); loadDips(); api.get("/admin/cards").then((r) => setCards(r.data)); api.get("/config").then((r) => setDays(r.data.days)); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <h2 data-testid="users-judges-heading" className="font-display text-2xl font-bold uppercase mt-6">Sędziowie</h2>
      <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {judges.map((j) => <JudgeCard key={j.number} j={j} onSaved={load} cards={cards} days={days} />)}
        <AddJudgeCard onSaved={load} />
      </div>
      <h2 className="font-display text-2xl font-bold uppercase mt-10">Stanowiska rejestracji</h2>
      <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {regs.map((j) => <JudgeCard key={j.number} j={j} onSaved={loadRegs} reg path="registrars" />)}
        <AddJudgeCard onSaved={loadRegs} path="registrars" title="Nowe stanowisko" />
      </div>
      <h2 className="font-display text-2xl font-bold uppercase mt-10">Konferansjer</h2>
      <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {anns.map((j) => <JudgeCard key={j.number} j={j} onSaved={loadAnns} reg path="announcers" />)}
        <AddJudgeCard onSaved={loadAnns} path="announcers" title="Nowy konferansjer" />
      </div>
      <h2 data-testid="users-diplomas-heading" className="font-display text-2xl font-bold uppercase mt-10">DYPLOM</h2>
      <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {dips.map((j) => <JudgeCard key={j.number} j={j} onSaved={loadDips} reg path="diplomas" />)}
        <AddJudgeCard onSaved={loadDips} path="diplomas" title="Nowy DYPLOM" />
      </div>
    </>
  );
};

const JudgeCard = ({ j, onSaved, reg, path, cards = [], days = [] }) => {
  const [name, setName] = useState(j.name);
  const [pin, setPin] = useState("");
  const [cardId, setCardId] = useState(j.card_id || "");
  const [jdays, setJdays] = useState(j.days || []);
  const pre = path ? path.slice(0, 3) : "judge";
  const save = async () => {
    try {
      await api.put(`/admin/${path || "judges"}/${j.number}`, { name, pin: pin || null, ...(reg ? {} : { card_id: cardId || null, days: jdays }) });
      toast.success(pin ? `Zapisano. Nowy PIN (${name}) aktywny – trzeba zalogować się ponownie.` : "Zapisano");
      setPin("");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    const warn = !reg && j.scores > 0 ? `\n\nUWAGA: ten sędzia ma ${j.scores} ocen – zostaną one usunięte i wyniki się przeliczą.` : "";
    if (!window.confirm(`Usunąć użytkownika "${j.name}" (nr ${j.number})?${warn}`)) return;
    try {
      await api.delete(reg ? `/admin/staff/${path}/${j.number}` : `/admin/judges/${j.number}`);
      toast.success("Użytkownik usunięty");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="fly-card p-5" data-testid={`${pre}-card-${j.number}`}>
      <div className="flex items-start justify-between">
        <div className={`font-display text-5xl font-black ${reg ? "text-cyan-400" : "text-blue-500"}`}>{j.number}</div>
        <button data-testid={`${pre}-delete-btn-${j.number}`} onClick={remove} title="Usuń użytkownika" className="h-9 w-9 rounded-lg text-red-400 hover:bg-red-500/10 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
      </div>
      {!reg && <div className="text-xs font-mono text-slate-500">{j.scores} ocen</div>}
      <label className="block text-xs text-slate-400 mt-3">Nazwa
        <input data-testid={`${pre}-name-input-${j.number}`} value={name} onChange={(e) => setName(e.target.value)} className={input} />
      </label>
      <label className="block text-xs text-slate-400 mt-3">Nowy PIN (4–8 cyfr)
        <input data-testid={`${pre}-pin-input-${j.number}`} value={pin} inputMode="numeric" onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} placeholder="bez zmian" className={`${input} font-mono tracking-widest`} />
      </label>
      {!reg && (
        <>
          <label className="block text-xs text-slate-400 mt-3">Karta punktacji
            <select data-testid={`judge-card-select-${j.number}`} value={cardId} onChange={(e) => setCardId(e.target.value)} className={input}>
              {cards.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <div className="text-xs text-slate-400 mt-3">Dni oceniania <span className="text-slate-500">(brak zaznaczenia = wszystkie)</span></div>
          <div className="flex flex-wrap gap-1.5 mt-1" data-testid={`judge-days-${j.number}`}>
            {days.map((d) => {
              const on = jdays.includes(d);
              return <button type="button" key={d} data-testid={`judge-day-${j.number}-${d.toLowerCase().replace(/[^a-z0-9ąćęłńóśźż]+/g, "-")}`}
                onClick={() => setJdays(on ? jdays.filter((x) => x !== d) : [...jdays, d])}
                className={`px-2 h-7 rounded-md text-[11px] border ${on ? "bg-blue-600 border-blue-500 text-white" : "border-white/10 text-slate-400"}`}>{d}</button>;
            })}
          </div>
        </>
      )}
      <button data-testid={`${pre}-save-btn-${j.number}`} onClick={save} className="mt-4 w-full h-10 rounded-lg bg-blue-600 hover:bg-blue-500 text-sm font-semibold flex items-center justify-center gap-2"><KeyRound className="w-4 h-4" />Zapisz</button>
    </div>
  );
};

const AddJudgeCard = ({ onSaved, path, title = "Nowy sędzia" }) => {
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const pre = path ? path.slice(0, 3) : "judge";
  const add = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(path ? `/admin/staff/${path}` : "/admin/judges", { name, pin });
      toast.success(`Dodano: ${data.name} (nr ${data.number})`);
      setName(""); setPin("");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={add} className="fly-card p-5 border-dashed border-blue-500/40" data-testid={`${pre}-add-card`}>
      <div className="font-display text-2xl font-bold uppercase flex items-center gap-2"><Plus className="w-6 h-6 text-blue-400" />{title}</div>
      <label className="block text-xs text-slate-400 mt-3">Nazwa
        <input data-testid={`${pre}-add-name-input`} value={name} onChange={(e) => setName(e.target.value)} placeholder="np. Anna Kowalska" className={input} />
      </label>
      <label className="block text-xs text-slate-400 mt-3">PIN (4–8 cyfr)
        <input data-testid={`${pre}-add-pin-input`} required value={pin} inputMode="numeric" onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} className={`${input} font-mono tracking-widest`} />
      </label>
      <button data-testid={`${pre}-add-btn`} disabled={pin.length < 4} className="mt-4 w-full h-10 rounded-lg bg-blue-600 hover:bg-blue-500 text-sm font-semibold disabled:opacity-40 flex items-center justify-center gap-2"><Plus className="w-4 h-4" />Dodaj</button>
      {!path && <p className="text-[11px] text-slate-500 mt-3">Po dodaniu sędziego wynik występu liczy się dopiero po ocenie wszystkich sędziów.</p>}
    </form>
  );
};
